<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Campaigns</h5>
        <p class="page-sub">Create and send targeted marketing campaigns</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon :icon="exporting ? 'spinner' : 'download'" :spin="exporting" class="me-1" />
            Export
            <font-awesome-icon icon="chevron-down" class="ms-1" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="doExport('csv')">
              <font-awesome-icon icon="file-csv" class="me-2" style="color:#166534" /> Export CSV
            </button>
            <button class="export-opt" @click="doExport('pdf')">
              <font-awesome-icon icon="file-pdf" class="me-2" style="color:#b91c1c" /> Export PDF
            </button>
          </div>
        </div>
        <button v-if="permission?.create_campaigns" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> New Campaign
        </button>
      </div>
    </div>

    <!-- Statistics -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="bullhorn" /></div>
        <div class="stat-body">
          <span class="stat-value">{{ statsLoading ? '—' : stats.total_campaigns.toLocaleString() }}</span>
          <span class="stat-label">Total Campaigns</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="users" /></div>
        <div class="stat-body">
          <span class="stat-value">{{ statsLoading ? '—' : stats.total_recipients.toLocaleString() }}</span>
          <span class="stat-label">Total Recipients</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon--green"><font-awesome-icon icon="envelope-open" /></div>
        <div class="stat-body">
          <span class="stat-value">{{ statsLoading ? '—' : stats.total_opened.toLocaleString() }}</span>
          <span class="stat-label">Total Emails Opened</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon stat-icon--orange"><font-awesome-icon icon="eye" /></div>
        <div class="stat-body">
          <span class="stat-value">{{ statsLoading ? '—' : stats.total_read.toLocaleString() }}</span>
          <span class="stat-label">Total Emails Read</span>
        </div>
      </div>
    </div>

    <div class="filter-bar">
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search campaigns..." />
      <select v-model="filterStatus" @change="page=1; fetchCampaigns()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="pending_approval">Pending Approval</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
        <option value="scheduled">Scheduled</option>
        <option value="sending">Sending</option>
        <option value="sent">Sent</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <div class="date-range">
        <input v-model="dateFrom" type="date" class="filter-input filter-input--date" placeholder="From" />
        <span class="date-sep">–</span>
        <input v-model="dateTo" type="date" class="filter-input filter-input--date" placeholder="To" />
        <button class="btn-apply" @click="applyDateFilter">Apply</button>
        <button v-if="dateFrom || dateTo" class="btn-clear-date" @click="clearDateFilter" title="Clear dates">✕</button>
      </div>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading campaigns...</p></div>
      <div v-else-if="!campaigns.length" class="empty-state">
        <font-awesome-icon icon="bullhorn" class="empty-icon" />
        <p>No campaigns found</p>
        <span>Create a campaign to reach your contacts</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Name</th><th>Type</th><th>Recipients</th><th>Status</th><th>Sent</th><th>Opens</th><th>Scheduled</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in campaigns" :key="c.id">
            <td class="name-col">{{ c.name }}</td>
            <td><span :class="['type-badge', `type--${c.type}`]">{{ fmtType(c.type) }}</span></td>
            <td>
              <span class="recipients-count">{{ fmtCount(c.recipients_count) }}</span>
              <span class="recipients-source">{{ recipientLabel(c) }}</span>
            </td>
            <td><span :class="['status-badge', `status--${c.status}`]">{{ c.status }}</span></td>
            <td>{{ c.sent_count ?? 0 }}</td>
            <td>{{ c.opened_count ?? 0 }}</td>
            <td>{{ c.scheduled_at ? fmtDate(c.scheduled_at) : '—' }}</td>
            <td>
              <div class="row-actions">
                <button v-if="permission?.edit_campaigns && ['draft','scheduled','rejected','pending_approval'].includes(c.status)" class="btn-link btn-link--edit" @click="openEdit(c)">Edit</button>
                <button v-if="['approved','sent'].includes(c.status)" class="btn-link btn-link--view" @click="openView(c)">View</button>
                <button v-if="permission?.create_campaigns && ['approved','sent'].includes(c.status)" class="btn-link btn-link--recreate" @click="openRecreate(c)">Recreate</button>
                <button v-if="permission?.send_campaigns && ['draft','approved'].includes(c.status)" class="btn-link btn-link--send" @click="sendCampaign(c)">Send</button>
                <button v-if="permission?.send_campaigns && c.status === 'scheduled'" class="btn-link btn-link--send" @click="sendCampaign(c)">Resend</button>
                <button v-if="permission?.delete_campaigns && ['draft','rejected'].includes(c.status)" class="btn-link btn-link--del" @click="confirmDelete(c)">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta && meta.last_page > 1" class="pagination">
        <button :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)" class="pg-btn">‹ Prev</button>
        <span class="pg-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
        <button :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)" class="pg-btn">Next ›</button>
      </div>
    </div>

    <!-- New / Edit Campaign Modal -->
    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box modal-box--campaign">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Campaign' : 'New Campaign' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body modal-body--campaign">

            <!-- ── Section: Basic ──────────────────────────────── -->
            <p class="section-label">Basic Info</p>
            <div class="form-group">
              <label class="form-label">Campaign Name <span class="req">*</span></label>
              <input v-model="form.name" class="form-input" required />
            </div>
            <div class="form-group">
              <label class="form-label">Type <span class="req">*</span></label>
              <select v-model="form.type" class="form-input">
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="push">Push Notification</option>
              </select>
            </div>

            <!-- ── Section: Recipients ────────────────────────── -->
            <div class="section-divider"></div>
            <p class="section-label">Recipients</p>
            <div class="form-group">
              <label class="form-label">Recipient Source <span class="req">*</span></label>
              <select v-model="form.recipient_type" @change="onRecipientTypeChange" class="form-input">
                <optgroup label="Groups">
                  <option value="contacts">All CRM Contacts</option>
                  <option value="leads">All Leads</option>
                  <option value="customers">All Customers</option>
                  <option value="employees">All Employees</option>
                  <option value="segment">CRM Segment</option>
                  <option value="customer_category">Customer Category</option>
                </optgroup>
                <optgroup label="Individual">
                  <option value="individual_customer">Specific Customer</option>
                  <option value="individual_employee">Specific Employee</option>
                </optgroup>
              </select>
            </div>

            <!-- Segment picker -->
            <div class="form-group" v-if="form.recipient_type === 'segment'">
              <label class="form-label">Segment <span class="req">*</span></label>
              <select v-model="form.segment_id" class="form-input">
                <option value="">Select segment...</option>
                <option v-for="s in segments" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>

            <!-- Customer category picker -->
            <div class="form-group" v-if="form.recipient_type === 'customer_category'">
              <label class="form-label">Customer Category <span class="req">*</span></label>
              <select v-model="form.customer_category_id" class="form-input">
                <option value="">Select category...</option>
                <option v-for="cat in customerCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
              </select>
            </div>

            <!-- Individual customer typeahead -->
            <div class="form-group" v-if="form.recipient_type === 'individual_customer'">
              <label class="form-label">Customer <span class="req">*</span></label>
              <div class="typeahead-wrap">
                <div v-if="form.individual_id" class="individual-selected">
                  <span class="individual-name">{{ form.individual_name }}</span>
                  <button type="button" class="individual-clear" @click="clearIndividual">
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
                <template v-else>
                  <input
                    v-model="individualSearch"
                    class="form-input"
                    placeholder="Search customer by name or email..."
                    @input="debouncedSearchCustomers"
                    autocomplete="off"
                  />
                  <div v-if="individualResults.length" class="typeahead-dropdown">
                    <div
                      v-for="r in individualResults" :key="r.id"
                      class="typeahead-item"
                      @mousedown.prevent="selectIndividual(r)"
                    >
                      <span class="ta-name">{{ r.customer_name }}</span>
                      <span class="ta-sub">{{ r.email }}</span>
                    </div>
                  </div>
                  <div v-else-if="individualLoading" class="typeahead-dropdown typeahead-loading">
                    Searching…
                  </div>
                </template>
              </div>
            </div>

            <!-- Individual employee typeahead -->
            <div class="form-group" v-if="form.recipient_type === 'individual_employee'">
              <label class="form-label">Employee <span class="req">*</span></label>
              <div class="typeahead-wrap">
                <div v-if="form.individual_id" class="individual-selected">
                  <span class="individual-name">{{ form.individual_name }}</span>
                  <button type="button" class="individual-clear" @click="clearIndividual">
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
                <template v-else>
                  <input
                    v-model="individualSearch"
                    class="form-input"
                    placeholder="Search employee by name or email..."
                    @input="debouncedSearchEmployees"
                    autocomplete="off"
                  />
                  <div v-if="individualResults.length" class="typeahead-dropdown">
                    <div
                      v-for="r in individualResults" :key="r.id"
                      class="typeahead-item"
                      @mousedown.prevent="selectIndividual({ id: r.id, customer_name: r.first_name + ' ' + r.last_name, email: r.email })"
                    >
                      <span class="ta-name">{{ r.first_name }} {{ r.last_name }}</span>
                      <span class="ta-sub">{{ r.email }}</span>
                    </div>
                  </div>
                  <div v-else-if="individualLoading" class="typeahead-dropdown typeahead-loading">
                    Searching…
                  </div>
                </template>
              </div>
            </div>

            <div class="recipient-note" v-if="form.type === 'push' && !['employees','individual_employee'].includes(form.recipient_type)">
              <font-awesome-icon icon="info-circle" class="note-icon" />
              Push notifications are delivered via the mobile app. Only employees with the app installed will receive them.
            </div>
            <div class="recipient-note" v-if="form.type !== 'push' && form.recipient_type === 'contacts'">
              <font-awesome-icon icon="info-circle" class="note-icon" />
              Only contacts with granted {{ form.type }} consent will receive this campaign.
            </div>

            <!-- ── Section: Content ───────────────────────────── -->
            <div class="section-divider"></div>
            <p class="section-label">Content</p>

            <!-- Email-specific -->
            <template v-if="form.type === 'email'">
              <div class="form-row-2">
                <div class="form-group">
                  <label class="form-label">From Name</label>
                  <input v-model="form.from_name" class="form-input" :placeholder="defaultFromName" />
                </div>
                <div class="form-group">
                  <label class="form-label">From Email</label>
                  <input v-model="form.from_email" type="email" class="form-input" :placeholder="defaultFromEmail" />
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Subject <span class="req">*</span></label>
                <input v-model="form.subject" class="form-input" required />
              </div>
              <div class="form-group">
                <label class="form-label">Body <span class="req">*</span></label>
                <div class="body-hint">Use <code>{{name}}</code>, <code>{{email}}</code>, <code>{{phone}}</code> as merge tags.</div>
                <div class="quill-wrap">
                  <QuillEditor
                    v-model:content="form.body"
                    content-type="html"
                    :toolbar="quillToolbar"
                    placeholder="Write your email message here…"
                    theme="snow"
                  />
                </div>
              </div>

              <!-- Attachments -->
              <div class="form-group">
                <label class="form-label">Attachments <span class="hint">(optional · max 10 MB each)</span></label>
                <!-- Existing saved attachments (edit mode) -->
                <div v-if="savedAttachments.length" class="attachment-list">
                  <div v-for="att in savedAttachments" :key="att.id" class="attachment-item">
                    <font-awesome-icon icon="paperclip" class="att-icon" />
                    <span class="att-name">{{ att.original_name }}</span>
                    <span class="att-size">{{ fmtBytes(att.size) }}</span>
                    <button type="button" class="att-del" @click="removeSavedAttachment(att)" :disabled="submitting" title="Remove">
                      <font-awesome-icon icon="times" />
                    </button>
                  </div>
                </div>
                <!-- Queued new files (pending upload) -->
                <div v-if="pendingFiles.length" class="attachment-list">
                  <div v-for="(f, i) in pendingFiles" :key="i" class="attachment-item attachment-item--pending">
                    <font-awesome-icon icon="paperclip" class="att-icon" />
                    <span class="att-name">{{ f.name }}</span>
                    <span class="att-size">{{ fmtBytes(f.size) }}</span>
                    <button type="button" class="att-del" @click="pendingFiles.splice(i, 1)" title="Remove">
                      <font-awesome-icon icon="times" />
                    </button>
                  </div>
                </div>
                <label class="att-add-btn">
                  <font-awesome-icon icon="plus" class="me-1" />
                  Add File
                  <input type="file" style="display:none" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.zip" @change="onFilePick" />
                </label>
              </div>
            </template>

            <!-- SMS-specific -->
            <template v-else-if="form.type === 'sms'">
              <div class="form-group">
                <label class="form-label">Message <span class="req">*</span></label>
                <div class="body-hint">Use <code>{{name}}</code>, <code>{{phone}}</code> as merge tags.</div>
                <textarea v-model="form.body" class="form-input body-textarea" rows="4" maxlength="612" required></textarea>
                <div class="char-count" :class="{ 'char-warn': smsCharCount > 160 }">
                  {{ smsCharCount }} chars · {{ smsParts }} part{{ smsParts > 1 ? 's' : '' }}
                </div>
              </div>
            </template>

            <!-- Push-specific -->
            <template v-else-if="form.type === 'push'">
              <div class="form-group">
                <label class="form-label">Notification Title <span class="req">*</span></label>
                <input v-model="form.subject" class="form-input" maxlength="65" required />
                <div class="char-count">{{ (form.subject || '').length }}/65</div>
              </div>
              <div class="form-group">
                <label class="form-label">Message <span class="req">*</span></label>
                <textarea v-model="form.body" class="form-input" rows="3" maxlength="240" required></textarea>
                <div class="char-count">{{ (form.body || '').length }}/240</div>
              </div>
              <div class="push-preview" v-if="form.subject || form.body">
                <div class="push-preview-header">
                  <div class="push-app-icon"><font-awesome-icon icon="bell" /></div>
                  <span class="push-app-name">Mapiibs</span>
                  <span class="push-time">now</span>
                </div>
                <div class="push-preview-title">{{ form.subject || 'Notification title' }}</div>
                <div class="push-preview-body">{{ form.body || 'Notification message...' }}</div>
              </div>
            </template>

            <!-- ── Approval info (create only) ───────────────── -->
            <template v-if="!editing">
              <div class="section-divider"></div>
              <div class="approval-notice">
                <font-awesome-icon icon="info-circle" class="notice-icon" />
                <div>
                  <strong>Approval required</strong>
                  <p class="notice-sub">This campaign will be submitted for approval before it can be sent. Once approved you can send or schedule it from the campaigns list.</p>
                </div>
              </div>
            </template>


          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">
              <template v-if="submitting">Saving…</template>
              <template v-else-if="!editing">
                <font-awesome-icon icon="paper-plane" class="me-1" />
                Submit for Approval
              </template>
              <template v-else>Save Changes</template>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete confirm -->
    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <span class="modal-title">Delete Campaign</span>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body"><p>Delete <strong>{{ deleteTarget?.name }}</strong>? This cannot be undone.</p></div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">{{ submitting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- Send / Schedule modal -->
    <div class="modal-overlay" :class="{ open: !!sendTarget }" @click.self="closeSendModal">
      <div class="modal-box modal-box--send">
        <div class="modal-header">
          <div class="modal-header-icon"><font-awesome-icon icon="paper-plane" /></div>
          <div>
            <span class="modal-title">Send Campaign</span>
            <p class="modal-sub">{{ sendTarget?.name }}</p>
          </div>
          <button class="modal-close" @click="closeSendModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="send-meta-row" style="margin-bottom:16px">
            <span class="send-meta">Type: <strong>{{ fmtType(sendTarget?.type) }}</strong></span>
            <span class="send-meta">Recipients: <strong>{{ recipientLabel(sendTarget) }}</strong></span>
          </div>

          <p class="send-section-label">When do you want to send?</p>
          <div class="send-options send-options--2">
            <label class="send-option" :class="{ active: sendModalMode === 'now' }">
              <input type="radio" v-model="sendModalMode" value="now" />
              <div class="send-option-content">
                <span class="send-option-title"><font-awesome-icon icon="paper-plane" class="me-1" />Send Now</span>
                <span class="send-option-desc">Dispatch immediately to all eligible recipients</span>
              </div>
            </label>
            <label class="send-option" :class="{ active: sendModalMode === 'later' }">
              <input type="radio" v-model="sendModalMode" value="later" />
              <div class="send-option-content">
                <span class="send-option-title"><font-awesome-icon icon="clock" class="me-1" />Schedule for Later</span>
                <span class="send-option-desc">Pick a date and time to send automatically</span>
              </div>
            </label>
          </div>

          <div v-if="sendModalMode === 'later'" class="form-group" style="margin-top:14px">
            <label class="form-label">Send Date &amp; Time <span class="req">*</span></label>
            <input v-model="sendScheduledAt" type="datetime-local" class="form-input" :min="minScheduledAt" />
            <div v-if="sendScheduledAt && isPast(sendScheduledAt)" class="schedule-warn">
              <font-awesome-icon icon="exclamation-triangle" /> Scheduled time must be in the future
            </div>
          </div>

          <p v-if="sendModalMode === 'now'" class="send-warning" style="margin-top:14px">
            <font-awesome-icon icon="exclamation-triangle" class="warn-icon" />
            This cannot be undone. The campaign will be sent immediately.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeSendModal">Cancel</button>
          <button
            class="btn-send"
            @click="doSend"
            :disabled="submitting || (sendModalMode === 'later' && (!sendScheduledAt || isPast(sendScheduledAt)))"
          >
            <font-awesome-icon :icon="sendModalMode === 'later' ? 'clock' : 'paper-plane'" class="me-1" />
            {{ submitting ? (sendModalMode === 'later' ? 'Scheduling…' : 'Sending…') : (sendModalMode === 'later' ? 'Schedule' : 'Send Now') }}
          </button>
        </div>
      </div>
    </div>
    <!-- View Campaign Drawer -->
    <div class="drawer-overlay" :class="{ open: viewDrawerOpen }" @click.self="closeView"></div>
    <div class="drawer" :class="{ open: viewDrawerOpen }">
      <div class="drawer-header">
        <div class="drawer-title-row">
          <span class="drawer-title">Campaign Details</span>
          <span v-if="viewTarget" :class="['status-badge', `status--${viewTarget.status}`]">{{ viewTarget.status }}</span>
        </div>
        <button class="drawer-close" @click="closeView"><font-awesome-icon icon="times" /></button>
      </div>

      <div v-if="viewLoading" class="drawer-loading"><div class="spinner"></div><p>Loading…</p></div>
      <div v-else-if="viewTarget" class="drawer-body">

        <!-- Basic info -->
        <p class="dv-section">Campaign Info</p>
        <div class="dv-grid">
          <div class="dv-item">
            <span class="dv-label">Name</span>
            <span class="dv-value">{{ viewTarget.name }}</span>
          </div>
          <div class="dv-item">
            <span class="dv-label">Type</span>
            <span class="dv-value"><span :class="['type-badge', `type--${viewTarget.type}`]">{{ fmtType(viewTarget.type) }}</span></span>
          </div>
          <div class="dv-item">
            <span class="dv-label">Recipients</span>
            <span class="dv-value">{{ recipientLabel(viewTarget) }}</span>
          </div>
          <div class="dv-item" v-if="viewTarget.scheduled_at">
            <span class="dv-label">Scheduled At</span>
            <span class="dv-value">{{ fmtDate(viewTarget.scheduled_at) }}</span>
          </div>
          <div class="dv-item" v-if="viewTarget.sent_at">
            <span class="dv-label">Sent At</span>
            <span class="dv-value">{{ fmtDate(viewTarget.sent_at) }}</span>
          </div>
          <div class="dv-item">
            <span class="dv-label">Sent</span>
            <span class="dv-value">{{ viewTarget.sent_count ?? 0 }} / {{ viewTarget.total_recipients ?? 0 }}</span>
          </div>
          <div class="dv-item" v-if="viewTarget.opened_count">
            <span class="dv-label">Opens</span>
            <span class="dv-value">{{ viewTarget.opened_count }}</span>
          </div>
        </div>

        <!-- Email-specific content -->
        <template v-if="viewTarget.type === 'email'">
          <div class="dv-divider"></div>
          <p class="dv-section">Email Content</p>
          <div class="dv-grid">
            <div class="dv-item" v-if="viewTarget.from_name">
              <span class="dv-label">From Name</span>
              <span class="dv-value">{{ viewTarget.from_name }}</span>
            </div>
            <div class="dv-item" v-if="viewTarget.from_email">
              <span class="dv-label">From Email</span>
              <span class="dv-value">{{ viewTarget.from_email }}</span>
            </div>
          </div>
          <div class="dv-item dv-item--full" v-if="viewTarget.subject">
            <span class="dv-label">Subject</span>
            <span class="dv-value">{{ viewTarget.subject }}</span>
          </div>
          <div class="dv-item dv-item--full" style="margin-top:10px" v-if="viewTarget.body">
            <span class="dv-label">Message Body</span>
            <div class="dv-body-frame" v-html="viewTarget.body"></div>
          </div>
        </template>

        <!-- SMS / Push content -->
        <template v-else>
          <div class="dv-divider"></div>
          <p class="dv-section">{{ viewTarget.type === 'push' ? 'Push Notification' : 'SMS' }} Content</p>
          <div class="dv-item dv-item--full" v-if="viewTarget.subject">
            <span class="dv-label">Title</span>
            <span class="dv-value">{{ viewTarget.subject }}</span>
          </div>
          <div class="dv-item dv-item--full" style="margin-top:10px" v-if="viewTarget.body">
            <span class="dv-label">Message</span>
            <div class="dv-plain-body">{{ viewTarget.body }}</div>
          </div>
        </template>

        <!-- Attachments -->
        <template v-if="viewTarget.attachments && viewTarget.attachments.length">
          <div class="dv-divider"></div>
          <p class="dv-section">Attachments</p>
          <div class="attachment-list">
            <a
              v-for="att in viewTarget.attachments"
              :key="att.id"
              :href="attUrl(att)"
              target="_blank"
              rel="noopener noreferrer"
              class="attachment-item attachment-item--link"
            >
              <font-awesome-icon icon="paperclip" class="att-icon" />
              <span class="att-name">{{ att.original_name }}</span>
              <span class="att-size">{{ fmtBytes(att.size) }}</span>
              <font-awesome-icon icon="external-link-alt" class="att-open-icon" />
            </a>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

const EMPTY_FORM = () => ({
  name: '', type: 'email',
  recipient_type: 'contacts', segment_id: '',
  individual_id: '', individual_name: '',
  customer_category_id: '',
  subject: '', body: '', from_name: '', from_email: '',
});

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

export default {
  components: { QuillEditor },
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    currentUser() { return this.store.state.getLoggedInUser; },
    smsCharCount() { return (this.form.body || '').length; },
    smsParts() { return Math.max(1, Math.ceil(this.smsCharCount / 160)); },
    defaultFromName() { return this.currentUser?.business_name || ''; },
    defaultFromEmail() { return this.currentUser?.business_email || ''; },
    minScheduledAt() {
      const d = new Date(Date.now() + 60000); // at least 1 minute ahead
      return d.toISOString().slice(0, 16);
    },
  },
  data() {
    return {
      campaigns: [], meta: null, loading: false, submitting: false,
      search: '', filterStatus: '', page: 1,
      dateFrom: '', dateTo: '',
      stats: { total_campaigns: 0, total_recipients: 0, total_opened: 0, total_read: 0 },
      statsLoading: false,
      exportMenuOpen: false, exporting: false,
      segments: [], customerCategories: [],
      modalOpen: false, editing: null, deleteTarget: null,
      sendTarget: null, sendModalMode: 'now', sendScheduledAt: '',
      viewDrawerOpen: false, viewTarget: null, viewLoading: false,
      form: EMPTY_FORM(),
      savedAttachments: [],
      pendingFiles: [],
      // individual typeahead state
      individualSearch: '', individualResults: [], individualLoading: false,
      individualSearchTimer: null,
      debounceTimer: null,
      quillToolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image', 'clean'],
      ],
    };
  },
  mounted() {
    this.fetchCampaigns();
    this.fetchStats();
    this.fetchSegments();
    this.fetchCustomerCategories();
    document.addEventListener('click', this.onOutsideClick);
  },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchCampaigns() {
      this.loading = true;
      try {
        const params = { search: this.search, status: this.filterStatus, page: this.page };
        if (this.dateFrom) params.from = this.dateFrom;
        if (this.dateTo)   params.to   = this.dateTo;
        const { data } = await axios.get('/crm/campaigns', { params });
        this.campaigns = data.data?.data ?? data.data ?? data ?? [];
        this.meta = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load campaigns'); }
      finally { this.loading = false; }
    },
    async fetchStats() {
      this.statsLoading = true;
      try {
        const params = {};
        if (this.dateFrom) params.from = this.dateFrom;
        if (this.dateTo)   params.to   = this.dateTo;
        const { data } = await axios.get('/crm/campaigns/stats', { params });
        if (data.status) this.stats = data.data;
      } catch { /* silent — stats are non-critical */ }
      finally { this.statsLoading = false; }
    },
    applyDateFilter() {
      this.page = 1;
      this.fetchCampaigns();
      this.fetchStats();
    },
    clearDateFilter() {
      this.dateFrom = '';
      this.dateTo   = '';
      this.page = 1;
      this.fetchCampaigns();
      this.fetchStats();
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) {
        this.exportMenuOpen = false;
      }
    },
    async doExport(format) {
      this.exportMenuOpen = false;
      this.exporting = true;
      try {
        const params = { format };
        if (this.search)       params.search = this.search;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.dateFrom)     params.from   = this.dateFrom;
        if (this.dateTo)       params.to     = this.dateTo;

        const { data } = await axios.get('/crm/campaigns/export', {
          params,
          responseType: 'blob',
        });

        const mime      = format === 'pdf' ? 'application/pdf' : 'text/csv';
        const ext       = format === 'pdf' ? 'pdf' : 'csv';
        const ts        = new Date().toISOString().slice(0, 10);
        const url       = URL.createObjectURL(new Blob([data], { type: mime }));
        const a         = document.createElement('a');
        a.href          = url;
        a.download      = `campaigns_${ts}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        this.toast.error('Export failed. Please try again.');
      } finally {
        this.exporting = false;
      }
    },
    async fetchSegments() {
      try {
        const { data } = await axios.get('/crm/segments', { params: { per_page: 200 } });
        this.segments = data.data?.data ?? data.data ?? [];
      } catch { /* silent */ }
    },
    async fetchCustomerCategories() {
      try {
        const { data } = await axios.get('/crm/account-categories');
        this.customerCategories = data.data?.data ?? data.data ?? [];
      } catch { /* silent */ }
    },
    debouncedFetch() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchCampaigns(); }, 400); },
    changePage(p) { this.page = p; this.fetchCampaigns(); },

    openCreate() {
      this.editing = null;
      this.form = { ...EMPTY_FORM(), from_name: this.defaultFromName, from_email: this.defaultFromEmail };
      this.savedAttachments = [];
      this.pendingFiles = [];
      this.resetIndividual();
      this.modalOpen = true;
    },
    async openEdit(c) {
      this.editing = c.id;
      this.form = {
        name:                 c.name,
        type:                 c.type ?? 'email',
        recipient_type:       c.recipient_type ?? 'contacts',
        segment_id:           c.segment_id ?? '',
        individual_id:        c.individual_id ?? '',
        individual_name:      c.individual_name ?? '',
        customer_category_id: c.customer_category_id ?? '',
        subject:              c.subject ?? '',
        body:                 c.body ?? '',
        from_name:            c.from_name  || this.defaultFromName,
        from_email:           c.from_email || this.defaultFromEmail,
      };
      this.savedAttachments = [];
      this.pendingFiles = [];
      this.resetIndividual();
      this.modalOpen = true;
      try {
        const { data } = await axios.get(`/crm/campaigns/${c.id}`);
        this.savedAttachments = data.data?.attachments ?? [];
      } catch { /* silent */ }
    },
    openRecreate(c) {
      this.editing = null;
      this.form = {
        name:                 c.name + ' (Copy)',
        type:                 c.type ?? 'email',
        recipient_type:       c.recipient_type ?? 'contacts',
        segment_id:           c.segment_id ?? '',
        individual_id:        c.individual_id ?? '',
        individual_name:      c.individual_name ?? '',
        customer_category_id: c.customer_category_id ?? '',
        subject:              c.subject ?? '',
        body:                 c.body ?? '',
        from_name:            c.from_name  || this.defaultFromName,
        from_email:           c.from_email || this.defaultFromEmail,
      };
      this.savedAttachments = [];
      this.pendingFiles = [];
      this.resetIndividual();
      this.modalOpen = true;
    },
    closeModal() {
      this.modalOpen = false;
      this.savedAttachments = [];
      this.pendingFiles = [];
      this.resetIndividual();
    },

    isPast(dt) { return dt && new Date(dt) <= new Date(); },

    onRecipientTypeChange() {
      this.form.individual_id = '';
      this.form.individual_name = '';
      this.form.segment_id = '';
      this.form.customer_category_id = '';
      this.resetIndividual();
    },

    resetIndividual() {
      this.individualSearch = '';
      this.individualResults = [];
      this.individualLoading = false;
    },

    debouncedSearchCustomers() {
      clearTimeout(this.individualSearchTimer);
      if (!this.individualSearch.trim()) { this.individualResults = []; return; }
      this.individualLoading = true;
      this.individualSearchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/settings/customers', { params: { query: this.individualSearch, length: 10 } });
          this.individualResults = data.data?.customers2 ?? data.data?.customers?.data ?? [];
        } catch { this.individualResults = []; }
        finally { this.individualLoading = false; }
      }, 300);
    },

    debouncedSearchEmployees() {
      clearTimeout(this.individualSearchTimer);
      if (!this.individualSearch.trim()) { this.individualResults = []; return; }
      this.individualLoading = true;
      this.individualSearchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/payroll/employees', { params: { search: this.individualSearch, per_page: 10 } });
          this.individualResults = data.data?.data ?? data.data ?? [];
        } catch { this.individualResults = []; }
        finally { this.individualLoading = false; }
      }, 300);
    },

    selectIndividual(r) {
      this.form.individual_id   = r.id;
      this.form.individual_name = r.customer_name;
      this.individualSearch   = '';
      this.individualResults  = [];
    },

    clearIndividual() {
      this.form.individual_id   = '';
      this.form.individual_name = '';
    },

    onFilePick(e) {
      const files = Array.from(e.target.files || []);
      const oversized = files.filter(f => f.size > MAX_FILE_BYTES);
      if (oversized.length) {
        this.toast.error(`${oversized.map(f => f.name).join(', ')} exceeds 10 MB limit`);
      }
      this.pendingFiles.push(...files.filter(f => f.size <= MAX_FILE_BYTES));
      e.target.value = '';
    },

    async uploadPendingFiles(campaignId) {
      for (const file of this.pendingFiles) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          await axios.post(`/crm/campaigns/${campaignId}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          this.toast.error(`Failed to upload ${file.name}: ${e.response?.data?.message ?? e.message}`);
        }
      }
      this.pendingFiles = [];
    },

    async removeSavedAttachment(att) {
      try {
        await axios.delete(`/crm/campaigns/${this.editing}/attachments/${att.id}`);
        this.savedAttachments = this.savedAttachments.filter(a => a.id !== att.id);
      } catch { this.toast.error('Failed to remove attachment'); }
    },
    confirmDelete(c) { this.deleteTarget = c; },

    async submit() {
      if (this.form.recipient_type === 'segment' && !this.form.segment_id) {
        this.toast.error('Please select a segment'); return;
      }
      if (this.form.recipient_type === 'customer_category' && !this.form.customer_category_id) {
        this.toast.error('Please select a customer category'); return;
      }
      if (['individual_customer', 'individual_employee'].includes(this.form.recipient_type) && !this.form.individual_id) {
        this.toast.error('Please search and select a recipient'); return;
      }
      this.submitting = true;
      try {
        const payload = { ...this.form };
        if (payload.recipient_type !== 'segment') delete payload.segment_id;
        if (payload.recipient_type !== 'customer_category') delete payload.customer_category_id;
        if (!['individual_customer', 'individual_employee'].includes(payload.recipient_type)) {
          delete payload.individual_id;
          delete payload.individual_name;
        }

        let campaignId = this.editing;
        if (this.editing) {
          await axios.put(`/crm/campaigns/${this.editing}`, payload);
          if (this.pendingFiles.length) {
            await this.uploadPendingFiles(campaignId);
          }
          this.toast.success('Campaign saved');
        } else {
          const { data } = await axios.post('/crm/campaigns', payload);
          campaignId = data.data?.id;
          if (this.pendingFiles.length && campaignId) {
            await this.uploadPendingFiles(campaignId);
          }
          this.toast.success('Campaign submitted for approval');
        }

        this.closeModal(); this.fetchCampaigns();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },

    async doDelete() {
      this.submitting = true;
      try {
        await axios.delete(`/crm/campaigns/${this.deleteTarget.id}`);
        this.toast.success('Deleted'); this.deleteTarget = null; this.fetchCampaigns();
      } catch { this.toast.error('Failed to delete'); }
      finally { this.submitting = false; }
    },

    async openView(c) {
      this.viewDrawerOpen = true;
      this.viewLoading = true;
      this.viewTarget = null;
      try {
        const { data } = await axios.get(`/crm/campaigns/${c.id}`);
        this.viewTarget = data.data ?? data;
      } catch { this.toast.error('Failed to load campaign'); this.viewDrawerOpen = false; }
      finally { this.viewLoading = false; }
    },
    closeView() { this.viewDrawerOpen = false; this.viewTarget = null; },

    sendCampaign(c) {
      this.sendTarget = c;
      this.sendModalMode = 'now';
      this.sendScheduledAt = '';
    },
    closeSendModal() {
      this.sendTarget = null;
      this.sendModalMode = 'now';
      this.sendScheduledAt = '';
    },
    async doSend() {
      this.submitting = true;
      try {
        if (this.sendModalMode === 'later') {
          const utcScheduledAt = new Date(this.sendScheduledAt).toISOString();
          const { data } = await axios.post(`/crm/campaigns/${this.sendTarget.id}/schedule`, { scheduled_at: utcScheduledAt });
          this.toast.success(data.message ?? `"${this.sendTarget.name}" scheduled`);
        } else {
          const { data } = await axios.post(`/crm/campaigns/${this.sendTarget.id}/send`);
          this.toast.success(data.message ?? `"${this.sendTarget.name}" is being sent`);
        }
        this.closeSendModal(); this.fetchCampaigns();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed'); }
      finally { this.submitting = false; }
    },

    recipientLabel(c) {
      if (!c) return '—';
      const map = {
        contacts:            'All Contacts',
        leads:               'All Leads',
        customers:           'All Customers',
        employees:           'All Employees',
        segment:             c.segment?.name ?? 'Segment',
        customer_category:   'Category',
        individual_customer: c.individual_name ?? 'Customer',
        individual_employee: c.individual_name ?? 'Employee',
      };
      return map[c.recipient_type] ?? c.recipient_type ?? '—';
    },
    fmtType(t) { return { email: 'Email', sms: 'SMS', push: 'Push' }[t] ?? t ?? '—'; },
    fmtCount(n) { return n == null ? '—' : Number(n).toLocaleString(); },
    fmtDate(v) { return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); },
    fmtBytes(b) {
      if (!b) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(b) / Math.log(k));
      return (b / Math.pow(k, i)).toFixed(i ? 1 : 0) + ' ' + sizes[i];
    },
    attUrl(att) {
      return att.url || (axios.defaults.baseURL.replace(/\/api\/?$/, '') + '/storage/' + att.path);
    },
  },
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
/* ── Stats grid ─────────────────────────────────────────────────────────── */
.stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px}
@media(max-width:900px){.stats-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:500px){.stats-grid{grid-template-columns:1fr}}
.stat-card{background:#fff;border:1.5px solid #e8edf5;border-radius:12px;padding:16px 18px;display:flex;align-items:center;gap:14px}
.stat-icon{width:42px;height:42px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
.stat-icon--blue{background:#e8f0fc;color:#004AAD}
.stat-icon--purple{background:#f0ebff;color:#6d28d9}
.stat-icon--green{background:#e8f8f0;color:#166534}
.stat-icon--orange{background:#fff4e6;color:#d97706}
.stat-body{display:flex;flex-direction:column;gap:2px;min-width:0}
.stat-value{font-size:22px;font-weight:700;color:#1a2b45;line-height:1.1}
.stat-label{font-size:12px;color:#7a90b0;white-space:nowrap}
/* ── Filter bar ─────────────────────────────────────────────────────────── */
.filter-bar{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;align-items:center}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;min-width:180px;outline:none}
.filter-input:focus{border-color:#004AAD}
.filter-input--date{flex:unset;min-width:130px}
.filter-select{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none}
.date-range{display:flex;align-items:center;gap:7px}
.date-sep{color:#7a90b0;font-size:14px;flex-shrink:0}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap}
.btn-apply:hover{background:#003b8e}
.btn-clear-date{background:none;border:1.5px solid #dde5f0;color:#7a90b0;padding:7px 9px;border-radius:8px;font-size:12px;cursor:pointer;line-height:1}
.btn-clear-date:hover{border-color:#e03535;color:#e03535}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger{background:#e03535;color:#fff;border:none;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.recipients-count{display:block;font-weight:700;font-size:13px;color:#1a2b45}
.recipients-source{display:block;font-size:11px;color:#7a90b0;margin-top:1px;text-transform:capitalize}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--send{color:#166534}
.btn-link--del{color:#e03535}
.type-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:uppercase;letter-spacing:.03em}
.type--email{background:#e0eaff;color:#004AAD}
.type--sms{background:#ede9fe;color:#6d28d9}
.type--push{background:#fef3c7;color:#92400e}
.status-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:capitalize}
.status--draft{background:#f1f5ff;color:#004AAD}
.status--pending_approval{background:#fef3c7;color:#92400e}
.status--approved{background:#dcfce7;color:#166534}
.status--rejected{background:#fee2e2;color:#dc2626}
.status--scheduled{background:#fef9e7;color:#b45309}
.status--sending{background:#ede9fe;color:#6d28d9}
.status--sent{background:#dcfce7;color:#166534}
.status--cancelled{background:#f3f4f6;color:#6b7280}
.pagination{display:flex;align-items:center;justify-content:center;gap:12px;padding:14px;border-top:1px solid #f0f4fa}
.pg-btn{padding:6px 14px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-info{font-size:13px;color:#7a90b0}
/* Modal */
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:520px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
.modal-box--sm{max-width:420px}
.modal-box--send{max-width:480px}
.modal-box--campaign{max-width:680px;overflow:visible}
.modal-body--campaign{overflow-y:auto;max-height:calc(90vh - 130px)}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit;box-sizing:border-box}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.form-row-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.form-row-3{display:flex;gap:14px}
.form-row-3 .form-group{flex:1}
.req{color:#e03535}
.hint{font-size:11px;color:#b0beca;font-weight:400}
.me-1{margin-right:4px}
/* Sections */
.section-label{font-size:11px;font-weight:800;color:#7a90b0;text-transform:uppercase;letter-spacing:.06em;margin:0 0 12px}
.section-divider{height:1px;background:#e8edf5;margin:18px 0 16px}
/* Body / merge-tag hint */
.body-hint{font-size:11.5px;color:#7a90b0;margin-bottom:5px}
.body-hint code{background:#f1f5ff;color:#004AAD;border-radius:4px;padding:1px 5px;font-size:11px}
.body-textarea{resize:vertical;min-height:120px}
/* SMS char counter */
.char-count{font-size:11.5px;color:#7a90b0;text-align:right;margin-top:4px}
.char-warn{color:#e03535;font-weight:600}
/* Push preview */
.push-preview{background:#1a2b45;border-radius:12px;padding:14px 16px;margin-top:10px}
.push-preview-header{display:flex;align-items:center;gap:8px;margin-bottom:8px}
.push-app-icon{width:22px;height:22px;border-radius:5px;background:#004AAD;color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px}
.push-app-name{font-size:12px;font-weight:600;color:#fff;flex:1}
.push-time{font-size:11px;color:#7a90b0}
.push-preview-title{font-size:13px;font-weight:700;color:#fff;margin-bottom:3px}
.push-preview-body{font-size:12px;color:#b0beca;line-height:1.5}
/* Recipient note */
.recipient-note{background:#f1f5ff;border-left:3px solid #004AAD;border-radius:0 6px 6px 0;padding:9px 12px;font-size:12.5px;color:#3d5068;display:flex;align-items:flex-start;gap:7px;margin-top:-4px;margin-bottom:14px}
.note-icon{color:#004AAD;flex-shrink:0;margin-top:2px}
/* send modal */
.send-section-label{font-size:12px;font-weight:700;color:#7a90b0;text-transform:uppercase;letter-spacing:.05em;margin:0 0 10px}
.modal-header-icon{width:36px;height:36px;border-radius:9px;background:#dcfce7;color:#166534;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.modal-sub{font-size:12px;color:#7a90b0;margin:2px 0 0}
.send-card{background:#f4f7fb;border-radius:10px;padding:14px 16px;margin-bottom:14px}
.send-name{font-size:14px;font-weight:700;color:#1a2b45;margin:0 0 6px}
.send-meta-row{display:flex;gap:16px}
.send-meta{font-size:13px;color:#7a90b0}
.send-warning{font-size:13px;color:#92400e;background:#fef3c7;border-radius:8px;padding:10px 12px;margin:0;display:flex;align-items:center;gap:8px}
.warn-icon{flex-shrink:0;color:#d97706}
.btn-send{background:#166534;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-send:hover{background:#14532d}
.btn-send:disabled{opacity:.55;cursor:not-allowed}
/* Typeahead */
.typeahead-wrap{position:relative}
.typeahead-dropdown{position:absolute;top:calc(100% + 4px);left:0;right:0;background:#fff;border:1.5px solid #dde5f0;border-radius:8px;box-shadow:0 6px 24px rgba(0,0,0,.1);z-index:200;max-height:200px;overflow-y:auto}
.typeahead-item{display:flex;justify-content:space-between;align-items:center;padding:9px 13px;cursor:pointer;font-size:13px;gap:10px}
.typeahead-item:hover{background:#f1f5ff}
.ta-name{font-weight:600;color:#1a2b45}
.ta-sub{font-size:12px;color:#7a90b0;text-align:right;white-space:nowrap}
.typeahead-loading{padding:10px 14px;font-size:13px;color:#7a90b0;font-style:italic}
.individual-selected{display:flex;align-items:center;gap:8px;background:#f1f5ff;border:1.5px solid #aac4f0;border-radius:8px;padding:8px 12px}
.individual-name{flex:1;font-size:13px;font-weight:600;color:#1a2b45}
.individual-clear{background:none;border:none;color:#7a90b0;cursor:pointer;font-size:13px;padding:2px 4px;border-radius:4px}
.individual-clear:hover{color:#e03535;background:#fee2e2}
/* Send Options */
.send-options{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}
.send-options--2{grid-template-columns:1fr 1fr}
.send-option{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;border:1.5px solid #e4eaf2;border-radius:10px;cursor:pointer;transition:border-color .15s,background .15s;background:#fff}
.send-option:hover{border-color:#aac4f0;background:#f8fbff}
.send-option.active{border-color:#004AAD;background:#f1f5ff}
.send-option input[type="radio"]{margin-top:2px;accent-color:#004AAD;flex-shrink:0}
.send-option-content{display:flex;flex-direction:column;gap:2px}
.send-option-title{font-size:13px;font-weight:700;color:#1a2b45}
.send-option-desc{font-size:11.5px;color:#7a90b0}
.schedule-warn{font-size:12px;color:#b45309;background:#fef3c7;border-radius:6px;padding:6px 10px;margin-top:5px;display:flex;align-items:center;gap:6px}
.approval-notice{background:#f0fdf4;border:1.5px solid #bbf7d0;border-radius:10px;padding:13px 16px;display:flex;align-items:flex-start;gap:11px;margin-bottom:4px}
.notice-icon{color:#166534;font-size:16px;flex-shrink:0;margin-top:2px}
.approval-notice strong{font-size:13px;color:#14532d;display:block;margin-bottom:3px}
.notice-sub{font-size:12.5px;color:#166534;margin:0;line-height:1.5}
/* Quill editor */
.quill-wrap{border:1.5px solid #dde5f0;border-radius:8px;overflow:hidden}
.quill-wrap:focus-within{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.quill-wrap :deep(.ql-toolbar){border:none;border-bottom:1.5px solid #dde5f0;background:#f8fafc;padding:6px 10px}
.quill-wrap :deep(.ql-container){border:none;font-size:13px;font-family:inherit}
.quill-wrap :deep(.ql-editor){min-height:160px;padding:9px 12px;color:#1a2b45;line-height:1.7}
.quill-wrap :deep(.ql-editor.ql-blank::before){color:#aab8cc;font-style:normal;left:12px}
/* Attachments */
.attachment-list{display:flex;flex-direction:column;gap:6px;margin-bottom:8px}
.attachment-item{display:flex;align-items:center;gap:8px;background:#f4f7fb;border:1px solid #e4eaf2;border-radius:8px;padding:7px 10px;font-size:13px}
.attachment-item--pending{background:#f1f5ff;border-color:#c7d9ff}
.att-icon{color:#7a90b0;font-size:12px;flex-shrink:0}
.att-name{flex:1;font-weight:500;color:#1a2b45;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.att-size{font-size:11.5px;color:#7a90b0;flex-shrink:0}
.att-del{background:none;border:none;color:#b0beca;cursor:pointer;padding:2px 4px;border-radius:4px;font-size:12px;flex-shrink:0;line-height:1}
.att-del:hover{color:#e03535;background:#fee2e2}
.att-del:disabled{opacity:.4;cursor:not-allowed}
.attachment-item--link{text-decoration:none;cursor:pointer}
.attachment-item--link:hover{background:#e0eaff;border-color:#aac4f0}
.att-open-icon{font-size:11px;color:#004AAD;flex-shrink:0;opacity:.7}
.att-add-btn{display:inline-flex;align-items:center;gap:5px;cursor:pointer;font-size:12.5px;font-weight:600;color:#004AAD;background:#f1f5ff;border:1.5px dashed #aac4f0;border-radius:8px;padding:7px 14px;transition:background .15s}
.att-add-btn:hover{background:#e0eaff}
/* View / Recreate buttons */
.btn-link--view{color:#7c3aed}
.btn-link--recreate{color:#d97706}
/* Drawer */
.drawer-overlay{position:fixed;inset:0;z-index:1100;background:rgba(15,25,50,.35);opacity:0;pointer-events:none;transition:opacity .25s}
.drawer-overlay.open{opacity:1;pointer-events:all}
.drawer{position:fixed;top:0;right:0;bottom:0;z-index:1101;width:480px;max-width:100vw;background:#fff;box-shadow:-8px 0 40px rgba(0,0,0,.12);transform:translateX(100%);transition:transform .28s cubic-bezier(.4,0,.2,1);display:flex;flex-direction:column;overflow:hidden}
.drawer.open{transform:translateX(0)}
.drawer-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2;flex-shrink:0}
.drawer-title-row{display:flex;align-items:center;gap:10px}
.drawer-title{font-size:16px;font-weight:700;color:#1a2b45}
.drawer-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.drawer-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:60px 24px;color:#7a90b0}
.drawer-body{flex:1;overflow-y:auto;padding:20px 22px}
.dv-section{font-size:11px;font-weight:800;color:#7a90b0;text-transform:uppercase;letter-spacing:.06em;margin:0 0 12px}
.dv-divider{height:1px;background:#e8edf5;margin:18px 0 16px}
.dv-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:4px}
.dv-item{display:flex;flex-direction:column;gap:3px}
.dv-item--full{display:flex;flex-direction:column;gap:5px}
.dv-label{font-size:11.5px;font-weight:600;color:#7a90b0;text-transform:uppercase;letter-spacing:.04em}
.dv-value{font-size:13.5px;color:#1a2b45;font-weight:500}
.dv-body-frame{border:1px solid #e4eaf2;border-radius:8px;background:#fafbfd;padding:16px 18px;font-family:'Helvetica Neue',Arial,sans-serif;font-size:14px;line-height:1.7;color:#1a2b45;max-height:360px;overflow-y:auto;margin-top:6px}
.dv-plain-body{border:1px solid #e4eaf2;border-radius:8px;background:#fafbfd;padding:12px 14px;font-size:13.5px;color:#1a2b45;white-space:pre-wrap;line-height:1.6;max-height:200px;overflow-y:auto;margin-top:6px}
</style>

<style>
/* Non-scoped: Quill HTML content inside .dv-body-frame (v-html bypasses scoped attributes) */
.dv-body-frame { box-sizing: border-box; }
.dv-body-frame p,
.dv-body-frame ol,
.dv-body-frame ul,
.dv-body-frame pre,
.dv-body-frame blockquote,
.dv-body-frame h1,
.dv-body-frame h2,
.dv-body-frame h3 { margin: 0; padding: 0; }
.dv-body-frame p        { margin-bottom: 10px; }
.dv-body-frame h1       { font-size: 2em;    font-weight: 700; line-height: 1.3; margin-bottom: 8px; }
.dv-body-frame h2       { font-size: 1.5em;  font-weight: 700; line-height: 1.3; margin-bottom: 6px; }
.dv-body-frame h3       { font-size: 1.17em; font-weight: 700; line-height: 1.3; margin-bottom: 6px; }
.dv-body-frame strong   { font-weight: 700; }
.dv-body-frame em       { font-style: italic; }
.dv-body-frame u        { text-decoration: underline; }
.dv-body-frame s        { text-decoration: line-through; }
.dv-body-frame a        { color: #004AAD; text-decoration: underline; }
.dv-body-frame blockquote { border-left: 4px solid #ccc; padding-left: 16px; margin: 5px 0; color: #5a6a85; }
.dv-body-frame pre      { background: #23241f; color: #f8f8f2; white-space: pre-wrap; padding: 5px 10px; border-radius: 3px; margin: 5px 0; }
.dv-body-frame img      { max-width: 100%; height: auto; display: block; border-radius: 4px; margin: 4px 0; }
.dv-body-frame table    { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
.dv-body-frame td,
.dv-body-frame th       { padding: 6px 10px; border: 1px solid #e4eaf2; }
.dv-body-frame th       { background: #f4f7fb; font-weight: 600; }
.dv-body-frame ol,
.dv-body-frame ul       { padding-left: 1.5em; margin-bottom: 10px; }
.dv-body-frame ul       { list-style-type: disc; }
.dv-body-frame ol       { list-style-type: decimal; }
.dv-body-frame li       { list-style: inherit; margin-bottom: 3px; }
.dv-body-frame ul ul    { list-style-type: circle; }
.dv-body-frame ul ul ul { list-style-type: square; }
.dv-body-frame .ql-indent-1 { padding-left: 3em; }
.dv-body-frame .ql-indent-2 { padding-left: 4.5em; }
.dv-body-frame .ql-indent-3 { padding-left: 6em; }
.dv-body-frame .ql-indent-4 { padding-left: 7.5em; }
.dv-body-frame .ql-align-center  { text-align: center; }
.dv-body-frame .ql-align-right   { text-align: right; }
.dv-body-frame .ql-align-justify { text-align: justify; }
</style>
